import React, { useState } from 'react';
import { supabase } from '../lib/supabase';

function generateCode() {
  // 8 chars from an unambiguous alphabet (no 0/O/1/I/L) — ~1 trillion combos
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  let out = '';
  for (let i = 0; i < 8; i++) out += chars[Math.floor(Math.random() * chars.length)];
  return out;
}

export default function RoomPage({ session, onJoin, currentRoom }) {
  const [tab, setTab] = useState('join');
  const [code, setCode] = useState('');
  const [shopName, setShopName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  async function handleJoin(e) {
    e.preventDefault();
    setLoading(true);
    setError('');

    // Join via the server-side "doorman" — it validates the code and adds us
    // without ever exposing the rooms table to the client.
    const { data, error: joinErr } = await supabase.rpc('join_room', { _code: code.trim().toUpperCase() });

    if (joinErr) { setError(joinErr.message); setLoading(false); return; }
    const room = data && data[0];
    if (!room) {
      setError('Room not found. Check the code and try again.');
      setLoading(false);
      return;
    }
    onJoin(room);
  }

  async function handleCreate(e) {
    e.preventDefault();
    setLoading(true);
    setError('');

    const newCode = generateCode();

    const { data: room, error: roomErr } = await supabase
      .from('rooms')
      .insert({ name: shopName, shop_name: shopName, code: newCode, owner_id: session.user.id })
      .select()
      .single();

    if (roomErr) { setError(roomErr.message); setLoading(false); return; }

    const { error: memberErr } = await supabase
      .from('room_members')
      .insert({
        room_id: room.id,
        user_id: session.user.id,
        role: 'admin',
        user_name: session.user.user_metadata?.full_name || session.user.email,
        avatar_url: session.user.user_metadata?.avatar_url || session.user.user_metadata?.picture || null,
      });

    if (memberErr) { setError(memberErr.message); setLoading(false); return; }
    onJoin({ ...room, role: 'admin' });
  }

  async function signOut() {
    await supabase.auth.signOut();
  }

  return (
    <div style={{
      display: 'flex', flexDirection: 'column', alignItems: 'center',
      justifyContent: 'center', height: '100%', background: 'var(--bg)', padding: '24px',
    }}>
      <div style={{ textAlign: 'center', marginBottom: 32 }}>
        <div style={{ fontFamily: 'var(--font-display)', fontSize: 38, fontWeight: 700, color: 'var(--text)' }}>
          SHOP<span style={{ color: 'var(--orange)' }}>TRACK</span>
        </div>
        <div style={{ color: 'var(--text3)', fontSize: 14, marginTop: 4 }}>
          Signed in as {session.user.email}
        </div>
      </div>

      {/* Show current room option if switching */}
      {currentRoom && (
        <div style={{
          background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 'var(--radius)',
          padding: '14px 20px', width: '100%', maxWidth: 400, marginBottom: 12,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <div>
            <div style={{ fontSize: 13, color: 'var(--text3)', marginBottom: 2 }}>Current room</div>
            <div style={{ fontSize: 15, fontWeight: 600 }}>{currentRoom.shop_name || currentRoom.name}</div>
            <div style={{ fontFamily: 'var(--font-mono)', fontSize: 13, color: 'var(--orange)', marginTop: 2 }}>{currentRoom.code}</div>
          </div>
          <button className="btn btn-primary" onClick={() => onJoin(currentRoom)} style={{ padding: '8px 16px', fontSize: 13 }}>
            Back
          </button>
        </div>
      )}

      <div style={{
        background: 'var(--bg2)', border: '1px solid var(--border)',
        borderRadius: 'var(--radius)', padding: '24px', width: '100%', maxWidth: 400,
      }}>
        <div style={{ display: 'flex', background: 'var(--bg3)', borderRadius: 'var(--radius-sm)', padding: 3, marginBottom: 24 }}>
          {['join', 'create'].map(t => (
            <button key={t} onClick={() => { setTab(t); setError(''); }} style={{
              flex: 1, padding: '8px', borderRadius: 5, border: 'none',
              fontSize: 14, fontWeight: 600,
              background: tab === t ? 'var(--bg)' : 'transparent',
              color: tab === t ? 'var(--text)' : 'var(--text3)',
              transition: 'all 0.15s',
            }}>
              {t === 'join' ? 'Join a Room' : 'Create Room'}
            </button>
          ))}
        </div>

        {tab === 'join' ? (
          <form onSubmit={handleJoin} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div className="field">
              <label className="label">Room Code</label>
              <input value={code} onChange={e => setCode(e.target.value)}
                placeholder="Enter room code"
                style={{ textTransform: 'uppercase', fontFamily: 'var(--font-mono)', letterSpacing: '0.1em', fontSize: 20 }}
                required maxLength={10} />
            </div>
            {error && <div style={{ color: 'var(--red)', fontSize: 14 }}>{error}</div>}
            <button className="btn btn-primary" type="submit" disabled={loading} style={{ width: '100%', padding: 14, fontSize: 15 }}>
              {loading ? <div className="spinner" /> : 'Join Room'}
            </button>
          </form>
        ) : (
          <form onSubmit={handleCreate} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div className="field">
              <label className="label">Shop Name</label>
              <input value={shopName} onChange={e => setShopName(e.target.value)} placeholder="e.g. Mike's Auto Body" required />
            </div>
            {error && <div style={{ color: 'var(--red)', fontSize: 14 }}>{error}</div>}
            <button className="btn btn-primary" type="submit" disabled={loading} style={{ width: '100%', padding: 14, fontSize: 15 }}>
              {loading ? <div className="spinner" /> : 'Create Room'}
            </button>
          </form>
        )}
      </div>

      <button onClick={signOut} className="btn btn-ghost" style={{ marginTop: 24, fontSize: 14 }}>
        Sign out
      </button>
    </div>
  );
}
